import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, ShieldAlert, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('validating'); // validating | valid | invalid | success
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await axios.get(`${API}/auth/validate-reset-token/${token}`);
      setEmail(res.data.email || '');
      setStatus('valid');
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Invalid or expired reset link');
      setStatus('invalid');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password/${token}`, { new_password: password });
      setStatus('success');
      toast.success('Password reset successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  // Validating state
  if (status === 'validating') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
          <p className="mt-4 text-stone-600">Validating your reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid / expired token
  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white p-8 rounded-2xl shadow-2xl text-center" data-testid="reset-invalid-card">
          <ShieldAlert className="mx-auto mb-4 text-red-500" size={56} />
          <h1 className="text-2xl font-bold text-stone-900 mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Invalid Reset Link
          </h1>
          <p className="text-stone-600 mb-6">{errorMsg}</p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#1A4D2E] text-white rounded-full px-8"
            data-testid="back-to-login-btn"
          >
            Back to Login
          </Button>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white p-8 rounded-2xl shadow-2xl text-center" data-testid="reset-success-card">
          <CheckCircle className="mx-auto mb-4 text-green-500" size={56} />
          <h1 className="text-2xl font-bold text-stone-900 mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            Password Reset!
          </h1>
          <p className="text-stone-600 mb-6">Your password has been updated. You can now log in with your new password.</p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-[#1A4D2E] text-white rounded-full px-8"
            data-testid="go-to-login-btn"
          >
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  // Valid token — show reset form
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A4D2E]/5 to-[#1A4D2E]/10 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-white p-8 rounded-2xl shadow-2xl" data-testid="reset-password-form">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#1A4D2E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="text-[#1A4D2E]" size={28} />
          </div>
          <h1 className="text-2xl font-bold text-stone-900" style={{fontFamily: '"Playfair Display", serif'}}>
            Set New Password
          </h1>
          {email && (
            <p className="text-stone-500 text-sm mt-1">for {email}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">
              New Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                className="pl-12 pr-12 h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg"
                required
                minLength={6}
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-2 block">
              Confirm Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
              <Input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="pl-12 pr-12 h-14 bg-stone-50 border-2 border-stone-200 focus:border-[#1A4D2E] focus:ring-0 rounded-lg"
                required
                minLength={6}
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={submitting || !password || !confirmPassword || password !== confirmPassword}
            className="w-full h-14 bg-[#1A4D2E] text-white text-lg rounded-lg hover:bg-[#143D24] disabled:opacity-50"
            data-testid="reset-submit-btn"
          >
            {submitting ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>

        <p className="text-center mt-4">
          <button
            onClick={() => navigate('/login')}
            className="text-stone-500 hover:text-[#1A4D2E] text-sm hover:underline"
          >
            Back to Login
          </button>
        </p>
      </Card>
    </div>
  );
};

export default ResetPassword;
